import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import BookingDetailContent from "./BookingDetailContent";
import RescheduleModal from "../../components/RescheduleModal";
import BookingContactBadge from "../../components/BookingContactBadge";

/**
 * Página dedicada de detalhe de reserva. Mantida para URLs diretos /
 * partilhas / bookmarks — a experiência principal agora é o modal dentro
 * de /cliente/reservas. Reutiliza exatamente o mesmo componente de conteúdo.
 */
export default function ClientBookingDetail() {
  const { bid } = useParams();
  const location = useLocation();
  const justCreated = Boolean(location.state?.justCreated);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${bid}`);
      setBooking(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);

  async function cancelBooking() {
    if (!window.confirm("Cancelar esta reserva?")) return;
    try {
      await api.patch(`/bookings/${bid}/status`, { status: "cancelled" });
      toast.success("Reserva cancelada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (loading) return <div className="text-sm text-[#a0b2af]">A carregar…</div>;
  if (!booking) return (
    <div className="space-y-4" data-testid="booking-not-found">
      <div className="text-sm text-[#fca5a5]">Reserva não encontrada.</div>
      <Link to="/cliente/reservas" className="text-xs uppercase tracking-widest text-[#d4af37] hover:underline">
        Voltar
      </Link>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="client-booking-detail">
      <div>
        <Link to="/cliente/reservas"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]"
              data-testid="back-to-bookings">
          <ArrowLeft size={12} /> Voltar às reservas
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl mt-3">
          {booking.apartment_name || `Reserva ${booking.apt_type}`}
        </h1>
      </div>
      {(justCreated || ["pending", "confirmed"].includes(booking.status)) && (
        <BookingContactBadge
          tone={justCreated || booking.status === "pending" ? "urgent" : "soft"}
          justCreated={justCreated}
        />
      )}
      <BookingDetailContent
        booking={booking}
        onCancel={cancelBooking}
        onEdit={() => setEditing(true)} />
      {editing && (
        <RescheduleModal
          booking={booking}
          onClose={() => setEditing(false)}
          onSaved={(fresh) => { setBooking(fresh); }}
        />
      )}
    </div>
  );
}
